import api from './authService'

// Types
export interface User {
  _id: string
  username: string
  name: string
  image: string | null
  phoneNumber: string
  latestLogin: string
  roles: string[]
  objectId: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  isEnable: boolean
  employeeCode?: string
  position?: string
  email?: string
}

export interface UserCreateInput {
  username: string
  password: string
  name: string
  phoneNumber?: string
  roles?: string[]
  isEnable?: boolean
  employeeCode?: string
  position?: string
  image?: string
  email?: string
}

export interface UserUpdateInput {
  name?: string
  phoneNumber?: string
  roles?: string[]
  isEnable?: boolean
  employeeCode?: string
  position?: string
  image?: string
  email?: string
}

export interface ChangePasswordInput {
  currentPassword: string
  newPassword: string
}

export interface UsersResponse {
  data: User[]
  meta: {
    total: number
    page: number
    perPage: number
    totalPages: number
  }
}

export interface PaginationParams {
  page: number
  perPage: number
  search?: string
  roles?: string[]
}

// User API service
const userService = {
  // GET /users/me - Get current user info
  getCurrentUser: async (): Promise<User> => {
    try {
      const response = await api.get('/users/me')
      return response.data
    } catch (error) {
      console.error('Error fetching current user:', error)
      throw error
    }
  },

  // GET /users - List all users
  getUsers: async (
    params: PaginationParams = { page: 1, perPage: 10 },
  ): Promise<{ users: User[]; pagination: any }> => {
    try {
      const response = await api.get('/users', { params })
      const apiData: UsersResponse = response.data

      return {
        users: apiData.data,
        pagination: apiData.meta,
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      throw error
    }
  },

  // GET /users/{username} - Get user by username
  getUser: async (username: string): Promise<User> => {
    try {
      const response = await api.get(`/users/${username}`)
      return response.data
    } catch (error) {
      console.error(`Error fetching user ${username}:`, error)
      throw error
    }
  },

  // POST /users - Create a new user
  createUser: async (userData: UserCreateInput): Promise<User> => {
    try {
      const response = await api.post('/users', userData)
      return response.data
    } catch (error) {
      console.error('Error creating user:', error)
      throw error
    }
  },

  // PATCH /users/{username} - Update user
  updateUser: async (
    username: string,
    userData: UserUpdateInput,
  ): Promise<User> => {
    try {
      const response = await api.patch(`/users/${username}`, userData)
      return response.data
    } catch (error) {
      console.error(`Error updating user ${username}:`, error)
      throw error
    }
  },

  // DELETE /users/{username} - Delete user
  deleteUser: async (username: string): Promise<boolean> => {
    try {
      await api.delete(`/users/${username}`)
      return true
    } catch (error) {
      console.error(`Error deleting user ${username}:`, error)
      throw error
    }
  },

  // PATCH /users/{username}/change-password - Change user password
  changePassword: async (
    username: string,
    passwordData: ChangePasswordInput,
  ): Promise<boolean> => {
    try {
      await api.patch(`/users/${username}/change-password`, passwordData)
      return true
    } catch (error) {
      console.error(`Error changing password for user ${username}:`, error)
      throw error
    }
  },

  searchUsers: async (
    keyword: string,
    params: PaginationParams = { page: 1, perPage: 10 },
  ): Promise<{ users: User[]; pagination: any }> => {
    try {
      const queryParams = {
        ...params,
        search: keyword,
      }

      const response = await api.get('/users', { params: queryParams })
      const apiData: UsersResponse = response.data

      return {
        users: apiData.data,
        pagination: apiData.meta,
      }
    } catch (error) {
      console.error('Error searching users:', error)
      throw error
    }
  },

  addUsersToProject: async (
    projectId: string,
    users: { username: string; role: string }[],
  ): Promise<boolean> => {
    try {
      await api.post(`/projects-users/${projectId}/batch`, { users })
      return true
    } catch (error) {
      console.error(`Error adding users to project ${projectId}:`, error)
      throw error
    }
  },
}

export default userService
